import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';

import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { TAvatarBackgroundColorIndex } from 'APP/components/Avatar/Avatar';
import { useUserProfile } from 'APP/main/hooks/userProfile/useUserProfile';
import { RouterState } from 'APP/router/constants';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';
import { Space } from 'STORE/Spaces/Space';
import { User } from 'STORE/Users/User/User';

import { IForwardMessageLocalStore } from './useForwardMessage.types';

export const useForwardMessage = (message: ChatMessage): IForwardMessageLocalStore => {
  const userProfile = useUserProfile(message.forward?.senderId, {
    groupId: message.groupId,
    messageId: message.id,
  });
  const { t } = useTranslation();
  const source = useAsObservableSource({ message });

  const presenter = useLocalStore<IForwardMessageLocalStore>(() => ({
    isLoading: false,

    get avatarTitle(): string {
      return source.message.forward?.avatarTitle || '';
    },

    get avatarUrl(): string {
      return source.message.avatarUrl || '';
    },

    get avatarColorIndex(): TAvatarBackgroundColorIndex {
      return source.message.forward?.avatarColorIndex || '0';
    },

    get group(): Group | null {
      return source.message.forward?.groupFrom || null;
    },

    get space(): Space | null {
      return presenter.group?.spaceId
        ? Entities.spacesStore.getById(presenter.group.spaceId)
        : null;
    },

    async loadGroupData(): Promise<void> {
      if (!source.message.forward || (presenter.group && !presenter.group.withoutMe)) {
        return;
      }

      presenter.isLoading = true;

      await Tasks.group.loadGroupsByIds([source.message.forward.groupId]);

      if (presenter.group?.spaceId && (!presenter.space || presenter.space.isGuest)) {
        await Tasks.space.loadSpace(presenter.group.spaceId);
      }

      presenter.isLoading = false;
    },

    async loadSenderData(): Promise<void> {
      if (!source.message.forward || (source.message.forward.senderUser as User)?.id) {
        return;
      }

      presenter.isLoading = true;
      await Tasks.relationships.loadUnknownUsersByIds({
        usersIds: [source.message.forward.senderId],
      });
      presenter.isLoading = false;
    },

    async onGroupClick(): Promise<void> {
      await presenter.loadGroupData();

      if (
        !presenter.group ||
        (presenter.group.isClosed && presenter.group.withoutMe) ||
        (presenter.group.spaceId &&
          (!presenter.space || (presenter.space.isPrivate && presenter.space.isGuest)))
      ) {
        Tasks.app.showAlert(t('channel_is_not_available'));

        return;
      }

      await Tasks.group.setActiveGroup({
        groupId: presenter.group.id,
        state: {
          [RouterState.hasGoBack]: true,
          [RouterState.groupId]: source.message.groupId,
          [RouterState.messageId]: source.message.id,
        },
      });
    },

    async onSenderClick(): Promise<void> {
      await presenter.loadSenderData();
      userProfile.show();
    },

    onClick(): void {
      if (presenter.isLoading) {
        return;
      }

      if (source.message.forward?.isFromChannel) {
        presenter.onGroupClick();
      } else {
        presenter.onSenderClick();
      }
    },
  }));

  return presenter;
};
